/**
 * What engagement rate is typical on Bluesky, by follower count.
 *
 * Measured, not borrowed: `scripts/research/bluesky-engagement-benchmark.mjs`
 * sampled accounts from Bluesky's public Jetstream firehose as they posted in
 * English, then ran THIS tool's own code on each (`fetchAccount`,
 * `analyseEngagement`): original posts only, 48 hours old or more, at least 20
 * of them, median post. Each band reports the quartiles of those per-account
 * medians. Public, unauthenticated data; only these aggregates were kept.
 *
 * Re-run the script before changing a number here, and update `sampledOn`.
 */

export type EngagementBand = {
  label: string;
  min: number;
  max: number;
  /** Accounts in the band after the tool's own readability rules. */
  accounts: number;
  /** Quartiles of the per-account median engagement rate, as percentages. */
  p25: number;
  median: number;
  p75: number;
};

export const BLUESKY_ENGAGEMENT_BENCHMARK = {
  sampledOn: "2026-09-27",
  /** Active English-language posters sampled from the firehose. */
  accountsSampled: 2000,
  /** Accounts with 50–100,000 followers and at least 20 measurable posts. */
  accountsMeasured: 1524,
  bands: [
    {
      label: "50–499",
      min: 50,
      max: 499,
      accounts: 504,
      p25: 0,
      median: 0.64,
      p75: 1.69,
    },
    {
      label: "500–1,999",
      min: 500,
      max: 1999,
      accounts: 536,
      p25: 0.09,
      median: 0.3,
      p75: 0.76,
    },
    {
      label: "2,000–9,999",
      min: 2000,
      max: 9999,
      accounts: 369,
      p25: 0.04,
      median: 0.11,
      p75: 0.33,
    },
    {
      label: "10,000–100,000",
      min: 10000,
      max: 100000,
      accounts: 115,
      p25: 0.01,
      median: 0.03,
      p75: 0.13,
    },
  ] satisfies EngagementBand[],
} as const;

/** The band an account of this size falls in, or null outside 50–100,000. */
export function engagementBandFor(followers: number): EngagementBand | null {
  return (
    BLUESKY_ENGAGEMENT_BENCHMARK.bands.find(
      (band) => followers >= band.min && followers <= band.max,
    ) ?? null
  );
}

/** Where a median rate sits against its band's middle half. */
export function positionInBand(
  rate: number,
  band: EngagementBand,
): "below" | "within" | "above" {
  if (rate < band.p25) {
    return "below";
  }

  return rate > band.p75 ? "above" : "within";
}
